import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import { Theme, makeStyles, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import * as _ from "lodash";
import React, { useState } from "react";
import {
  Label,
  Legend,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { numberFormat } from "../utilities";
import EffectChartTooltipContent from "./EffectChartTooltipContent";
import ProgressOverlay from "./ProgressOverlay";
import { EffectsDataState } from "src/types";

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
}));

interface Props {
  effectsData: EffectsDataState["effectsData"];
  loading: boolean;
}

const EffectChart: React.FC<Props> = ({ effectsData, loading }: Props) => {
  const classes = useStyles();

  const [hoverScatterIndex, setHoverScatterIndex] = useState<number | null>(
    null
  );
  const theme = useTheme<Theme>();

  const getData = () => {
    const result = _.values(
      _.reduce(
        effectsData,
        (carry: any, item) => {
          const label = `${item.column_name} ${item.column_value}`;
          return {
            ...carry,
            [label]: {
              ...{ affected_rows: 0, label: "", CLV: 0, "Churn Score": 0 },
              ...(carry[label] || {}),
              affected_rows: Math.max(
                (carry[label] || {}).affected_rows || 0,
                item.affected_rows
              ),
              label,
              [item.target_name]: item.effect,
            },
          };
        },
        {}
      )
    );
    return result;
  };

  const handleMouseEnter = (scatter: any, scatterIndex: number) => {
    setHoverScatterIndex(scatterIndex);
  };

  const handleMouseLeave = (scatter: any, scatterIndex: number) => {
    setHoverScatterIndex(null);
  };

  return (
    <Paper className={classes.paper}>
      <ProgressOverlay loading={loading}>
        <Grid container={true} spacing={2}>
          <Grid item={true} xs={12}>
            <Typography variant="h6">Effekte</Typography>
          </Grid>
          <Grid item={true} xs={12}>
            <ResponsiveContainer>
              <Typography component="div">
                <ScatterChart
                  width={1200}
                  height={700}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <XAxis
                    dataKey="CLV"
                    name="Einfluss auf CLV"
                    type="number"
                    domain={[-6, 6]}
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                  />
                  <YAxis
                    type="number"
                    dataKey="Churn Score"
                    name="Einfluss auf Churn Score"
                    domain={[-6, 6]}
                    axisLine={false}
                    tickLine={false}
                    tick={false}
                  />
                  <ZAxis
                    type="number"
                    dataKey="affected_rows"
                    range={[100, 1000]}
                    name="Betroffene Kunden"
                  />
                  <ReferenceLine y={0} stroke="black" />
                  <ReferenceLine x={0} stroke="black" />
                  <Tooltip
                    formatter={(v: any) => numberFormat(Number(v), false)}
                    cursor={{ strokeDasharray: "3 3" }}
                    content={(props: any) => (
                      <EffectChartTooltipContent {...props} />
                    )}
                  />
                  <Legend
                    align="right"
                    layout="vertical"
                    verticalAlign="middle"
                    width={500}
                    wrapperStyle={{
                      paddingLeft: "57px",
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  />
                  <ReferenceArea x1={-6} x2={0} y1={-6} y2={0} fill="none">
                    <Label
                      value="CLV ↓ / Churn Score ↓"
                      position="center"
                      fill="lightgray"
                    />
                  </ReferenceArea>
                  <ReferenceArea x1={0} x2={6} y1={-6} y2={0} fill="none">
                    <Label
                      value="CLV ↑ / Churn Score ↓"
                      position="center"
                      fill="lightgray"
                    />
                  </ReferenceArea>
                  <ReferenceArea x1={-6} x2={0} y1={0} y2={6} fill="none">
                    <Label
                      value="CLV ↓ / Churn Score ↑"
                      position="center"
                      fill="lightgray"
                    />
                  </ReferenceArea>
                  <ReferenceArea x1={0} x2={6} y1={0} y2={6} fill="none">
                    <Label
                      value="CLV ↑ / Churn Score ↑"
                      position="center"
                      fill="lightgray"
                    />
                  </ReferenceArea>
                  <ReferenceArea x1={-6} x2={6} y1={-6} y2={6} fill="none">
                    <Label position="top" value="Churn Score ↑" />
                    <Label position="bottom" value="Churn Score ↓" />
                    <Label position="right" value="CLV ↑" />
                    <Label position="left" value="CLV ↓" />
                  </ReferenceArea>
                  {getData().map((d, index) => (
                    <Scatter
                      key={d.label}
                      name={d.label}
                      data={[d]}
                      fill={
                        hoverScatterIndex === index
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main
                      }
                    />
                  ))}
                </ScatterChart>
              </Typography>
            </ResponsiveContainer>
          </Grid>
        </Grid>
      </ProgressOverlay>
    </Paper>
  );
};

export default EffectChart;



// WEBPACK FOOTER //
// ./src/components/EffectChart.tsx